import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';
import { Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const [formData, setFormData] = useState({
    establishment_name: '',
    printer_ip: '',
    printer_type: 'network',
    currency: 'EUR',
    print_template: '',
    print_values: true,
    pos_mode: 'pdv',
    payment_methods: 'Dinheiro,Cartão,PIX',
    ask_table_number: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData({
        establishment_name: response.data.establishment_name || '',
        printer_ip: response.data.printer_ip || '',
        printer_type: response.data.printer_type || 'network',
        currency: response.data.currency || 'EUR',
        print_template: response.data.print_template || '',
        print_values: response.data.print_values !== false,
        pos_mode: response.data.pos_mode || 'pdv',
        payment_methods: response.data.payment_methods || 'Dinheiro,Cartão,PIX',
        ask_table_number: response.data.ask_table_number === true
      });
    } catch (error) {
      console.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, formData);
      alert('Configurações salvas com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('Erro ao salvar configurações');
      alert('Erro ao salvar configurações');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Configurações">
        <div className="text-white/60 font-inter">Carregando...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configurações">
      <div className="max-w-2xl">
        <p className="text-white/60 font-inter mb-6">Configure os parâmetros do sistema</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
            <h3 className="text-xl font-oswald font-bold text-white mb-4">Informações Gerais</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="establishment_name" className="text-white/80">
                  Nome do Estabelecimento
                </Label>
                <Input
                  id="establishment_name"
                  data-testid="establishment-name-input"
                  value={formData.establishment_name}
                  onChange={(e) => setFormData({ ...formData, establishment_name: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  placeholder="BÉNI RESTAURANT"
                  required
                />
                <p className="text-white/40 text-sm mt-1 font-inter">
                  Este nome aparecerá nos tickets impressos
                </p>
              </div>

              <div>
                <Label htmlFor="currency" className="text-white/80">
                  Moeda
                </Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger data-testid="currency-select" className="bg-[#27272a] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10 text-white">
                    <SelectItem value="BRL">Real (R$)</SelectItem>
                    <SelectItem value="EUR">Euro (€)</SelectItem>
                    <SelectItem value="USD">Dólar ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pos_mode" className="text-white/80">
                  Modo de Operação
                </Label>
                <Select
                  value={formData.pos_mode}
                  onValueChange={(value) => setFormData({ ...formData, pos_mode: value })}
                >
                  <SelectTrigger data-testid="pos-mode-select" className="bg-[#27272a] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10 text-white">
                    <SelectItem value="pdv">PDV (Venda Direta)</SelectItem>
                    <SelectItem value="comanda">Comandas</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-white/40 text-sm mt-1 font-inter">
                  PDV: venda imediata | Comandas: mesa/cliente com itens acumulados
                </p>
              </div>

              <div>
                <Label htmlFor="payment_methods" className="text-white/80">
                  Formas de Pagamento
                </Label>
                <Input
                  id="payment_methods"
                  value={formData.payment_methods}
                  onChange={(e) => setFormData({ ...formData, payment_methods: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  placeholder="Dinheiro,Cartão,PIX"
                />
                <p className="text-white/40 text-sm mt-1 font-inter">
                  Separar por vírgula. Ex: Dinheiro,Cartão,PIX
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#27272a] rounded-lg">
                <div>
                  <Label className="text-white/80">Solicitar Número da Mesa</Label>
                  <p className="text-white/40 text-sm font-inter">
                    Pedir mesa ao iniciar venda/comanda
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, ask_table_number: !formData.ask_table_number })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    formData.ask_table_number ? 'bg-[#FF5500]' : 'bg-white/20'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      formData.ask_table_number ? 'left-8' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
            <h3 className="text-xl font-oswald font-bold text-white mb-4">Configuração de Impressora</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="printer_type" className="text-white/80">
                  Tipo de Impressora
                </Label>
                <Select
                  value={formData.printer_type}
                  onValueChange={(value) => setFormData({ ...formData, printer_type: value })}
                >
                  <SelectTrigger data-testid="printer-type-select" className="bg-[#27272a] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10 text-white">
                    <SelectItem value="network">Impressora de Rede (EPSON 80mm)</SelectItem>
                    <SelectItem value="windows">Impressora Windows (via navegador)</SelectItem>
                    <SelectItem value="none">Sem Impressora</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.printer_type === 'network' && (
                <div>
                  <Label htmlFor="printer_ip" className="text-white/80">
                    IP da Impressora EPSON (Rede)
                  </Label>
                  <Input
                    id="printer_ip"
                    data-testid="printer-ip-input"
                    value={formData.printer_ip}
                    onChange={(e) => setFormData({ ...formData, printer_ip: e.target.value })}
                    className="bg-[#27272a] border-white/10 text-white"
                    placeholder="192.168.1.100"
                  />
                  <p className="text-white/40 text-sm mt-1 font-inter">
                    Endereço IP da impressora térmica EPSON 80mm na rede (porta 9100)
                  </p>
                </div>
              )}

              {formData.printer_type === 'windows' && (
                <div className="bg-[#27272a] border border-[#FF5500]/30 rounded-lg p-4">
                  <p className="text-white/80 text-sm font-inter">
                    Ao finalizar uma venda, o ticket será enviado automaticamente para a impressora padrão do Windows.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-[#27272a] rounded-lg">
                <div>
                  <Label className="text-white/80">Imprimir Valores</Label>
                  <p className="text-white/40 text-sm font-inter">
                    Exibir preços e total no ticket
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, print_values: !formData.print_values })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    formData.print_values ? 'bg-[#FF5500]' : 'bg-white/20'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      formData.print_values ? 'left-8' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <Label htmlFor="print_template" className="text-white/80">
                  Mensagem Personalizada do Ticket
                </Label>
                <Textarea
                  id="print_template"
                  data-testid="print-template-input"
                  value={formData.print_template}
                  onChange={(e) => setFormData({ ...formData, print_template: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  placeholder="Ex: Obrigado pela preferência!"
                  rows={3}
                />
                <p className="text-white/40 text-sm mt-1 font-inter">
                  Texto adicional que aparecerá no cabeçalho do ticket (opcional)
                </p>
              </div>
            </div>
          </div>

          <Button
            data-testid="save-settings-button"
            type="submit"
            className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white font-oswald text-lg h-12"
          >
            <Save className="w-5 h-5 mr-2" />
            Salvar Configurações
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default Settings;